"use client";

import * as React from "react";
import { MenuIcon, Sun, Moon, Monitor } from "lucide-react";
import { useTheme } from "next-themes";
import {
  Sheet,
  SheetContent,
  SheetFooter,
} from "@workspace/ui/components/sheet";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { AccountMenu } from "@workspace/ui/components/account-menu";
import { AppFrameHeaderToggle } from "@workspace/ui/components/app-frame";
import { NotificationBell } from "@workspace/ui/components/notification-bell";
import { BrandThemeSwitcher } from "@workspace/ui/components/brand-theme-switcher";
import { useAuth, useRequireScopes } from "@workspace/ui/hooks/useAuth";
import Link from "next/link";
import { Logo } from "./icons";
import { useStageOrigin } from "@workspace/ui/lib/stage-url";

export type HeaderVariant = "floating" | "fixed";

export interface AppHeaderProps {
  variant?: HeaderVariant;
  appName?: string;
  children?: React.ReactNode;
  className?: string;
}

const variantClasses: Record<HeaderVariant, string> = {
  floating: cn(
    "sticky top-0 md:top-5 z-50",
    "mx-auto container md:rounded-full border shadow",
    "bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur-lg",
  ),
  fixed: cn(
    "sticky top-0 z-50 w-full border-b",
    "bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur-lg",
  ),
};

// Brand-theme picker mounted in header for admins, on local dev only.
// Env-gate runs first to avoid a network call (useRequireScopes hits the auth
// API) on every page load in non-local environments. Production builds tree-
// shake the entire component out via the early null return.
//
// Signed in, light/dark/system lives in the account menu (`ThemeChoice` in
// account-menu.tsx), so the standalone toggle below is for signed-out visitors
// only: they have no menu to hold it. useAuth starts signed-out on the first
// render, so a signed-in page shows the toggle for one frame, the same swap
// AccountMenu makes from "Sign in" to the avatar.
//
// Wherever light/dark is chosen (toggle or account menu), it and the picker
// cooperate:
// next-themes owns the dark/light class on <html>, and `useBrandTheme` reads
// next-themes' resolvedTheme to decide which per-mode bucket to apply (so a
// user-picked dark mode flips brand-aware vars too, when the brand has dark
// data). When the brand only has one mode and the user picks the other,
// applyTheme silently falls back to the brand's defaultMode for vars it
// covers, while the codebase's `.dark` block still fires for un-overridden
// vars (chart-*, sidebar, etc) because next-themes' class stays set.
function AdminThemeControls() {
  const { authenticated } = useAuth();
  const enabled = process.env.NEXT_PUBLIC_THEME_PICKER_ENABLED === "true";
  if (!authenticated) {
    return <ThemeToggle />;
  }
  if (!enabled) {
    return null;
  }
  return <AdminThemeControlsInner />;
}

function AdminThemeControlsInner() {
  const { granted } = useRequireScopes("admin:*");
  if (!granted) {
    return null;
  }
  return (
    <div className="flex items-center gap-1">
      <BrandThemeSwitcher
        editorHref={
          // Injected per stage by build-stage.ts; literal is the local
          // fallback and must stay last (tdd-0093).
          process.env.NEXT_PUBLIC_DESIGN_SYSTEM_URL ||
          "https://design-system.l.gaz.codes/themes"
        }
      />
    </div>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <Button size="icon" variant="ghost" className="rounded-full">
        <Monitor className="size-4" aria-hidden="true" />
        {/* Matches the mounted control's label so the accessible name doesn't
            change when hydration swaps this out. */}
        <span className="sr-only">Toggle theme</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="rounded-full">
          {theme === "dark" ? (
            <Moon className="size-4" />
          ) : theme === "light" ? (
            <Sun className="size-4" />
          ) : (
            <Monitor className="size-4" />
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme("light")}>
          <Sun className="mr-2 size-4" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")}>
          <Moon className="mr-2 size-4" />
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")}>
          <Monitor className="mr-2 size-4" />
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppHeader({
  variant = "floating",
  appName,
  children,
  className,
}: AppHeaderProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const headerRef = React.useRef<HTMLElement>(null);
  // The platform home on THIS stage (`l.gaz.codes` on local), not always prod.
  const homeUrl = useStageOrigin(undefined, "https://gaz.codes");

  // Publish the rendered header height as `--global-header-height` on
  // documentElement, so consumers can use `calc(100dvh - var(--global-header-height))`
  // without hard-coding the height. Falls back to the static value declared in
  // packages/ui/src/styles/globals.css if measurement isn't available.
  React.useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const apply = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty(
          "--global-header-height",
          `${Math.round(h)}px`
        );
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <header ref={headerRef} className={cn(variantClasses[variant], className)}>
      <nav className="mx-auto flex items-center justify-between px-2 py-2">
        {/* LEFT: the app sidebar's toggle, then the brand.

            The toggle is here because it is the only chrome on every route: a
            docked sidebar the viewer collapsed renders nothing of its own, so
            a pane-level button would leave them stranded on any pane that has
            none. Renders nothing for an app with no left side (AppFrame). */}
        <div className="flex items-center gap-1">
          <AppFrameHeaderToggle />
          <Link
            href={homeUrl}
            className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-full duration-100"
          >
            <Logo className="size-9 text-primary" />
            <p
              className={`font-mono text-base font-bold ${appName !== "web" && "hidden"} md:flex`}
            >
              gaz.codes
              <span className="text-muted-foreground pl-2">/</span>
            </p>
          </Link>
          {appName && (
            <>
              <Link
                href="/"
                className="hover:bg-accent rounded-full px-2 py-1 font-mono text-sm font-medium duration-100"
              >
                {appName}
              </Link>
            </>
          )}
        </div>

        {/* CENTER: Sub-app content slot */}
        {children && (
          <div className="hidden flex-1 items-center justify-center lg:flex">
            {children}
          </div>
        )}

        {/* RIGHT: theme controls (the standalone toggle when signed out; the
            brand picker for admins on local dev) + Notifications + Account
            menu, which holds light/dark when signed in + Mobile menu.

            The bell lives here rather than in each app because RootLayout
            renders this header for every app on the platform — one mount
            reaches all of them (tdd-0060 §5). It renders null when signed out. */}
        <div className="flex items-center gap-2">
          <AdminThemeControls />
          <NotificationBell />
          <AccountMenu />
          {children && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <Button
                size="icon"
                variant="outline"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
                className="lg:hidden"
              >
                <MenuIcon className="size-4" aria-hidden="true" />
              </Button>
              <SheetContent
                className="bg-background/95 supports-[backdrop-filter]:bg-background/80 gap-0 backdrop-blur-lg"
                showClose={false}
                side="left"
              >
                <div className="grid gap-y-2 overflow-y-auto px-4 pt-12 pb-5">
                  {children}
                </div>
                <SheetFooter />
              </SheetContent>
            </Sheet>
          )}
        </div>
      </nav>
    </header>
  );
}
